/* eslint-disable */

import { take, call, select, fork, put, all, delay } from 'redux-saga/effects';
import { callHUB } from './services/checkHUBTokens';
import * as HUB from './services/hub';
import * as LOCAL from './services/local';

import { push as RouterPush, push as RouterReplace } from 'redux-first-history';

// eslint-disable-next-line import/no-unresolved
import AppConfig from 'appconfig';
import i18n from 'i18n-react';
import moment from 'moment';
import { host } from './utils';

const getState = (state) => state.appData;
const getRouting = (state) => state.router.location;

// Reset functions
function* resetCompanies(credentialStore) {
    while (true) {
        const { data } = yield take('RESET_COMPANIES');
        yield put({ type: 'RESET', dataType: 'companies' });
    }
}

function* resetCompany(credentialStore) {
    while (true) {
        const { data } = yield take('RESET_COMPANY_DATA');
        yield put({ type: 'RESET', dataType: 'company' });
    }
}

function* resetAdministrations(credentialStore) {
    while (true) {
        const { data } = yield take('RESET_ADMINISTRATIONS');
        yield put({ type: 'RESET', dataType: 'administrations' });
    }
}

function* resetAdministration(credentialStore) {
    while (true) {
        const { data } = yield take('RESET_ADMINISTRATION_DATA');
        yield put({ type: 'RESET', dataType: 'administration' });
    }
}

function* resetLicense(credentialStore) {
    while (true) {
        const { data } = yield take('RESET_LICENSE_DATA');
        yield put({ type: 'RESET', dataType: 'license' });
    }
}

function* resetApplication(credentialStore) {
    while (true) {
        const { data } = yield take('RESET_APPLICATION_DATA');
        yield put({ type: 'RESET', dataType: 'administration' });
    }
}

function* resetApiUser(credentialStore) {
    while (true) {
        const { data } = yield take('RESET_API_USER_DATA');
        yield put({ type: 'RESET', dataType: 'apiUser' });
        yield put({ type: 'RESET', dataType: 'apiUserNewEdit' });
        yield put({ type: 'RESET', dataType: 'apiUserWithAllRoutingSlugs' });
    }
}

function* resetBreadcrumbs(credentialStore) {
    while (true) {
        const { data } = yield take('RESET_BREADCRUMBS');
        yield put({ type: 'RESET', dataType: 'breadcrumbs' });
    }
}

function* resetSearchResults(credentialStore) {
    while (true) {
        const { data } = yield take('RESET_SEARCH_RESULTS');
        yield put({ type: 'RESET', dataType: 'searchResults' });
    }
}

function* resetRoutingSlugs(credentialStore) {
    while (true) {
        const { data } = yield take('RESET_ROUTING_SLUGS');
        yield put({ type: 'RESET', dataType: 'routingSlugs' });
    }
}

function* resetTemplate(credentialStore) {
    while (true) {
        const { data } = yield take('RESET_TEMPLATE');
        yield put({ type: 'RESET', dataType: 'template' });
    }
}

function* resetTemplateWithAllRoutes(credentialStore) {
    while (true) {
        const { data } = yield take('RESET_TEMPLATE_WITH_ALL_ROUTING_SLUGS');
        yield put({ type: 'RESET', dataType: 'templateWithAllRoutingSlugs' });
    }
}

// Custom alert functions
function createAlert(typeOfAlert, message, data) {
    if (typeOfAlert === 'error') {
        const errorResponse = JSON.parse(data.error.message);
        errorResponse.error_codes = [];
        errorResponse.error_codes.push({
            'nl-NL': message,
        });
        data.error.message = JSON.stringify(errorResponse);
    } else if (typeOfAlert === 'success') {
        const successResponse = data.message;
        successResponse.success_codes = [];
        successResponse.success_codes.push({
            'nl-NL': message,
        });
    }
}

// Check if the refresh token has expired function
function hasTokenExpired(token) {
    let isExpired = false;
    try {
        const tokenObject = JSON.parse(atob(token.split('.')[1]));
        const expirationDate = moment(tokenObject.exp * 1000).utc();

        if (expirationDate <= moment().utc()) {
            isExpired = true;
        }
    } catch (error) {
        console.error('Cannot parse token, assume expired', error);
        isExpired = true;
    }

    return isExpired;
}

// ------

function* changeEnvironmentFlow(credentialStore) {
    while (true) {
        const { data } = yield take('CHANGE_ENVIRONMENT');

        credentialStore.changeEnvironment(data.activeEnvironmentName);
        yield put(RouterPush('/login'));
    }
}

function* setTokensFlow(credentialStore) {
    while (true) {
        const { accessToken, refreshToken } = yield take('SET_TOKENS');
        yield credentialStore.setTokens(accessToken, refreshToken);
    }
}

function* loginFlow(credentialStore) {
    while (true) {
        const { data } = yield take('LOGIN');

        yield put({ type: 'USER_LOGIN_REQUEST' });

        const loginData = yield call(HUB.authenticateLogin, credentialStore, {
            ...data,
            hostname: host(),
        });

        if (loginData.error) {
            yield put({ type: 'LOGIN_FAILED', errors: loginData, dataType: 'errors' });

            // Create a custom error, since FlexWeb HUB provides none
            let message = i18n.translate('account.alert.error.submittedData');

            if (!!loginData.error.message) {
                let status = JSON.parse(loginData.error.message).status;

                if (status === 0) {
                    // Status 0 means no response.
                    message = i18n.translate('general.alert.error.connectionErrorWithHUB');
                } else if (status >= 500) {
                    // Just tell the status code instead of generic auth error.
                    message = 'Server error: Status ' + status + '.';
                }
            }
            createAlert('error', message, loginData);

            yield put({ type: 'ERROR', errors: loginData, dataType: 'alerts' });
        } else {
            credentialStore.setTokens(loginData.message.data.access_token, loginData.message.data.refresh_token);

            // Get the info of the latest git commit
            const gitInfoData = yield call(LOCAL.getGitInfo);
            if (!gitInfoData.error) {
                yield put({ type: 'SUCCESS', gitInfo: gitInfoData, dataType: 'gitInfo' });
            }

            yield put({ type: 'LOGGED_IN' });
            yield put(RouterPush('/administrations'));
        }
    }
}

function* refreshAuthenticationFlow(credentialStore) {
    while (true) {
        const { data } = yield take('REFRESH_LOGIN_AUTH');
        yield put({ type: 'USER_LOGIN_REQUEST' });

        // Set the last known used environment in the credentialStore
        credentialStore.setLastUsedEnvironment();

        // Redirect user to the environment selection screen if no last used environment is known
        if (
            typeof credentialStore.getEnvironment() == 'undefined' ||
            credentialStore.getEnvironment() == 'null' ||
            credentialStore.getEnvironment() === null
        ) {
            yield put({ type: 'LOGIN_FAILED' });
            continue;
        }

        // Redirect user to the login page if no refresh token exists or if the refresh token has expired
        if (
            credentialStore.getTokens().refreshToken === null ||
            hasTokenExpired(credentialStore.getTokens().refreshToken)
        ) {
            yield put({ type: 'LOGIN_FAILED' });
            continue;
        }

        const loginData = yield call(HUB.refreshAuthentication, credentialStore.getTokens().refreshToken);

        if (loginData.error) {
            yield put({ type: 'LOGIN_FAILED' });
        } else {
            yield put({
                type: 'SET_TOKENS',
                accessToken: loginData.message.data.access_token,
                refreshToken: loginData.message.data.refresh_token,
            });

            // Get the info of the latest git commit
            const gitInfoData = yield call(LOCAL.getGitInfo);
            if (!gitInfoData.error) {
                yield put({ type: 'SUCCESS', gitInfo: gitInfoData, dataType: 'gitInfo' });
            }

            yield put({ type: 'LOGGED_IN' });

            const currentRoute = yield select(getRouting);
            if (currentRoute.pathname === '/login' || currentRoute.pathname === '/') {
                yield put(RouterReplace('/administrations'));
            }
        }
    }
}

function* logoutFlow(credentialStore) {
    while (true) {
        const { data } = yield take('LOGOUT');

        // Mark the user as logged out
        yield put({ type: 'USER_LOGOUT_REQUEST' });
        const hubRequest = yield callHUB(HUB.logout, credentialStore);
        if (hubRequest.error) {
            console.error('Logout error', hubRequest);
        }

        // Delete the credential tokens
        credentialStore.deleteTokens();

        // Mark the user as logged out
        yield put({ type: 'LOGGED_OUT' });

        // Redirect to the logout page which is de DefaultPage
        yield put(RouterPush('/logout'));
    }
}

function* companiesFlow(credentialStore) {
    while (true) {
        const { data } = yield take('LOAD_COMPANIES');

        const state = yield select(getState);
        const allowCached = data && data.allowCached;

        if (!state.companies || state.companies.length === 0 || !allowCached) {
            yield put({ type: 'DATA_LOADING', resource: 'companies' });

            let companiesData = {};

            // Don't transform the metadata if there isn't any
            if (typeof data !== 'undefined') {
                companiesData = yield callHUB(HUB.getCompanies, credentialStore, data);
            } else {
                companiesData = yield callHUB(HUB.getCompanies, credentialStore);
            }

            if (companiesData.error) {
                yield put({ type: 'ERROR', errors: companiesData, dataType: 'alerts' });
            } else {
                yield put({ type: 'SUCCESS', companies: companiesData, dataType: 'companies' });
                yield put({ type: 'DATA_LOADED', resource: 'companies' });
            }
        }
    }
}

function* allCompaniesFlow(credentialStore) {
    while (true) {
        const { payload } = yield take('LOAD_ALL_COMPANIES');

        yield put({ type: 'DATA_LOADING', resource: 'companies' });

        let page = 1;
        const resultsPerPage = 500;
        const metaData = {
            meta: {
                resultsPerPage: resultsPerPage,
                page: page,
                ...payload?.meta,
            },
        };

        const companiesData = yield callHUB(HUB.getCompanies, credentialStore, metaData);

        if (companiesData.error) {
            yield put({ type: 'ERROR', errors: companiesData, dataType: 'alerts' });
            yield put({ type: 'DATA_LOAD_ERROR', resource: 'companies' });
        } else {
            // Fetch all other companies if there are more than the hard-coded limit of 100
            const metadata = companiesData.message.meta;
            const metadataLimit = parseInt(metadata.limit, 10);
            const metadataOffset = parseInt(metadata.offset, 10);
            let amountOfResults = metadataLimit + metadataOffset;

            while (amountOfResults < metadata.total) {
                // Get the next page of data
                page++;
                const newMetadata = {
                    meta: {
                        resultsPerPage: resultsPerPage,
                        page: page,
                    },
                };

                const extraCompaniesData = yield callHUB(HUB.getCompanies, credentialStore, newMetadata);

                // Fuse the newly fetched data to the existing routing slugs array
                companiesData.message.data.push(...extraCompaniesData.message.data);

                // Update the amount of results
                const newOffset = parseInt(extraCompaniesData.message.meta.offset, 10);
                amountOfResults = metadataLimit + newOffset;
            }

            yield put({ type: 'SUCCESS', companies: companiesData, dataType: 'companies' });
            yield put({ type: 'DATA_LOADED', resource: 'companies' });
        }
    }
}

function* companyFlow(credentialStore) {
    while (true) {
        const { data } = yield take('LOAD_COMPANY');

        yield put({ type: 'DATA_LOADING', resource: 'company' });

        const companyDetailData = yield callHUB(HUB.getCompany, credentialStore, data.companyId);

        if (companyDetailData.error) {
            yield put({ type: 'ERROR', errors: companyDetailData, dataType: 'alerts' });
        } else {
            yield put({ type: 'SUCCESS', company: companyDetailData, dataType: 'company' });
            yield put({ type: 'DATA_LOADED', resource: 'company' });
        }
    }
}

function* postCompanyFlow(credentialStore) {
    while (true) {
        const { data } = yield take('POST_COMPANY');

        yield put({ type: 'SET_DATA', resource: 'companyNew' });

        const companyNewData = yield callHUB(HUB.postCompany, credentialStore, data);

        if (companyNewData.error) {
            yield put({ type: 'ERROR', errors: companyNewData, dataType: 'alerts' });
            yield put({ type: 'SET_DATA_ERROR', resource: 'companyNew' });
        } else {
            createAlert('success', i18n.translate('company.alert.success.save'), companyNewData);
            yield put({ type: 'SUCCESS', success: companyNewData, dataType: 'alerts' });

            yield put({ type: 'SET_DATA_SUCCESS', resource: 'companyNew' });
            yield put({ type: 'RESET', dataType: 'companyNew' });

            yield put(RouterPush('/companies/' + companyNewData.message.data.id));
            yield put({ type: 'LOAD_COMPANIES', data: {} });
        }
    }
}

function* putCompanyFlow(credentialStore) {
    while (true) {
        const { data } = yield take('PUT_COMPANY');

        yield put({ type: 'SET_DATA', resource: 'companyEdit' });

        const companyEditData = yield callHUB(HUB.putCompany, credentialStore, data);

        if (companyEditData.error) {
            yield put({ type: 'ERROR', errors: companyEditData, dataType: 'alerts' });
            yield put({ type: 'SET_DATA_ERROR', resource: 'companyEdit' });
        } else {
            createAlert('success', i18n.translate('general.alert.success.editData'), companyEditData);
            yield put({ type: 'SUCCESS', success: companyEditData, dataType: 'alerts' });

            yield put({ type: 'SET_DATA_SUCCESS', resource: 'companyEdit' });
            yield put({ type: 'RESET', dataType: 'companyEdit' });

            yield put({ type: 'RESET_COMPANY_DATA', data: {} });
            yield put({ type: 'RESET_BREADCRUMBS', data: {} });

            yield put(RouterPush('/companies/' + companyEditData.message.data.id));
            yield put({ type: 'LOAD_COMPANIES', data: {} });
        }
    }
}

function* putLicensePackageLinkFlow(credentialStore) {
    while (true) {
        const { data } = yield take('PUT_LICENSE_PACKAGE_LINK');

        yield put({ type: 'SET_DATA', resource: 'licensePackageLink' });

        const licensePackageLinkData = yield callHUB(HUB.setLicensePackageLink, credentialStore, data);

        if (licensePackageLinkData.error) {
            yield put({ type: 'ERROR', errors: licensePackageLinkData, dataType: 'alerts' });
            yield put({ type: 'SET_DATA_ERROR', resource: 'licensePackageLink' });
        } else {
            createAlert('success', i18n.translate('general.alert.success.editData'), licensePackageLinkData);
            yield put({ type: 'SUCCESS', success: licensePackageLinkData, dataType: 'alerts' });

            yield put({ type: 'SET_DATA_SUCCESS', resource: 'licensePackageLink' });
            yield put({ type: 'RESET', dataType: 'licensePackageLink' });

            yield put({ type: 'RESET_COMPANY_DATA', data: {} });
            yield put({ type: 'RESET_BREADCRUMBS', data: {} });

            yield put(RouterPush('/companies/' + data.companyId + '/licenses/' + data.licenseId));
            yield put({ type: 'LOAD_COMPANIES', data: {} });
        }
    }
}

function* deleteLicensePackageLinkFlow(credentialStore) {
    while (true) {
        const { data } = yield take('DELETE_LICENSE_PACKAGE_LINK');

        yield put({ type: 'DATA_DELETE', resource: 'licensePackageLink' });

        const licensePackageLinkData = yield callHUB(HUB.deleteLicensePackageLink, credentialStore, data);

        if (licensePackageLinkData.error) {
            yield put({ type: 'ERROR', errors: licensePackageLinkData, dataType: 'alerts' });
        } else {
            createAlert('success', i18n.translate('packageLinks.alert.success.delete'), licensePackageLinkData);
            yield put({ type: 'SUCCESS', success: licensePackageLinkData, dataType: 'alerts' });

            yield put({ type: 'DELETE_DATA_SUCCESS', resource: 'license' });
            yield put(RouterPush('/companies/' + data.companyId + '/licenses/' + data.licenseId));
        }
    }
}

function* putAdministrationPackageLinkFlow(credentialStore) {
    while (true) {
        const { data } = yield take('PUT_ADMINISTRATION_PACKAGE_LINK');

        yield put({ type: 'SET_DATA', resource: 'administrationPackageLink' });

        const administrationPackageLinkData = yield callHUB(HUB.setAdministrationPackageLink, credentialStore, data);

        if (administrationPackageLinkData.error) {
            yield put({ type: 'ERROR', errors: administrationPackageLinkData, dataType: 'alerts' });
            yield put({ type: 'SET_DATA_ERROR', resource: 'administrationPackageLink' });
        } else {
            createAlert('success', i18n.translate('general.alert.success.editData'), administrationPackageLinkData);
            yield put({ type: 'SUCCESS', success: administrationPackageLinkData, dataType: 'alerts' });

            yield put({ type: 'SET_DATA_SUCCESS', resource: 'administrationPackageLink' });
            yield put({ type: 'RESET', dataType: 'administrationPackageLink' });

            yield put({ type: 'RESET_COMPANY_DATA', data: {} });
            yield put({ type: 'RESET_BREADCRUMBS', data: {} });

            yield put(
                RouterPush(
                    '/companies/' +
                        data.companyId +
                        '/licenses/' +
                        data.licenseId +
                        '/administrations/' +
                        data.administrationId,
                ),
            );
            yield put({ type: 'LOAD_ADMINISTRATIONS', data: {} });
        }
    }
}

function* deleteAdministrationPackageLinkFlow(credentialStore) {
    while (true) {
        const { data } = yield take('DELETE_ADMINISTRATION_PACKAGE_LINK');

        yield put({ type: 'DATA_DELETE', resource: 'administrationPackageLink' });

        const administrationPackageLinkData = yield callHUB(HUB.deleteAdministrationPackageLink, credentialStore, data);

        if (administrationPackageLinkData.error) {
            yield put({ type: 'ERROR', errors: administrationPackageLinkData, dataType: 'alerts' });
        } else {
            createAlert('success', i18n.translate('license.alert.success.delete'), administrationPackageLinkData);
            yield put({ type: 'SUCCESS', success: administrationPackageLinkData, dataType: 'alerts' });

            yield put({ type: 'DELETE_DATA_SUCCESS', resource: 'administrationPackageLink' });
            yield put(
                RouterPush(
                    '/companies/' +
                        data.companyId +
                        '/licenses/' +
                        data.licenseId +
                        '/administrations/' +
                        data.administrationId,
                ),
            );
        }
    }
}

function* administrationsFlow(credentialStore) {
    while (true) {
        const { data } = yield take('LOAD_ADMINISTRATIONS');

        const state = yield select(getState);
        const allowCached = data && data.allowCached;

        if (!state.administrations || state.administrations.length == 0 || !allowCached) {
            yield put({ type: 'DATA_LOADING', resource: 'administrations' });

            const administrationsData = yield callHUB(HUB.getAdministrations, credentialStore, data);
            if (administrationsData.error) {
                yield put({ type: 'ERROR', errors: administrationsData, dataType: 'alerts' });
            } else {
                yield put({ type: 'SUCCESS', administrations: administrationsData, dataType: 'administrations' });
                yield put({ type: 'DATA_LOADED', resource: 'administrations' });
            }
        }
    }
}

function* allAdministrationsFlow(credentialStore) {
    while (true) {
        const { data } = yield take('LOAD_ALL_ADMINISTRATIONS');

        yield put({ type: 'DATA_LOADING', resource: 'administrations' });

        let page = 1;
        const resultsPerPage = 100;
        const metadata = {
            meta: {
                resultsPerPage: resultsPerPage,
                page: page,
            },
        };

        let administrationsData = yield callHUB(HUB.getAdministrations, credentialStore);

        if (administrationsData.error) {
            yield put({ type: 'ERROR', errors: administrationsData, dataType: 'alerts' });
            yield put({ type: 'DATA_LOAD_ERROR', resource: 'administrations' });
        } else {
            // Fetch all other companies if there are more than the hard-coded limit of 100
            const metadata = administrationsData.message.meta;
            const metadataLimit = parseInt(metadata.limit, 10);
            const metadataOffset = parseInt(metadata.offset, 10);
            let amountOfResults = metadataLimit + metadataOffset;

            while (amountOfResults < metadata.total) {
                // Get the next page of data
                page++;
                const newMetadata = {
                    meta: {
                        resultsPerPage: resultsPerPage,
                        page: page,
                    },
                };

                const extraAdministrationsData = yield callHUB(HUB.getAdministrations, credentialStore, newMetadata);

                // Fuse the newly fetched data to the existing routing slugs array
                administrationsData.message.data.push(...extraAdministrationsData.message.data);

                // Update the amount of results
                const newOffset = parseInt(extraAdministrationsData.message.meta.offset, 10);
                amountOfResults = metadataLimit + newOffset;
            }

            yield put({ type: 'SUCCESS', administrations: administrationsData, dataType: 'administrations' });
            yield put({ type: 'DATA_LOADED', resource: 'administrations' });
        }
    }
}

function* administrationFlow(credentialStore) {
    while (true) {
        const { data } = yield take('LOAD_ADMINISTRATION');

        yield put({ type: 'DATA_LOADING', resource: 'administration' });

        const administrationData = yield callHUB(HUB.getAdministration, credentialStore, data.administrationId);

        if (administrationData.error) {
            yield put({ type: 'ERROR', errors: administrationData, dataType: 'alerts' });
        } else {
            yield put({ type: 'SUCCESS', administration: administrationData, dataType: 'administration' });
            yield put({ type: 'DATA_LOADED', resource: 'administration' });
        }
    }
}

function* administrationDetailFlow(credentialStore) {
    while (true) {
        const { data } = yield take('LOAD_ADMINISTRATION_DETAIL');

        yield put({ type: 'DATA_LOADING', resource: 'administration' });

        const administrationData = yield callHUB(HUB.getAdministration, credentialStore, data.administrationId);

        if (administrationData.error) {
            yield put({ type: 'ERROR', errors: administrationData, dataType: 'alerts' });
        } else {
            yield put({ type: 'SUCCESS', administration: administrationData, dataType: 'administration' });
            yield put({ type: 'DATA_LOADED', resource: 'administration' });
        }

        // Get the detail data of the company which is connected to this administration
        if (administrationData.message.data.company_id === null) {
            yield put({ type: 'SUCCESS', company: null, dataType: 'company' });
            yield put({ type: 'DATA_LOADED', resource: 'company' });
        } else {
            yield put({ type: 'DATA_LOADING', resource: 'company' });

            const companyData = yield callHUB(
                HUB.getCompany,
                credentialStore,
                administrationData.message.data.company_id,
            );

            if (companyData.error) {
                yield put({ type: 'ERROR', errors: companyData, dataType: 'alerts' });
            } else {
                yield put({ type: 'SUCCESS', company: companyData, dataType: 'company' });
                yield put({ type: 'DATA_LOADED', resource: 'company' });
            }
        }

        // Get the detail data of the license which is connected to this administration
        if (administrationData.message.data.license_id === null) {
            yield put({ type: 'SUCCESS', license: null, dataType: 'license' });
            yield put({ type: 'DATA_LOADED', resource: 'license' });
        } else {
            const licenseData = yield callHUB(
                HUB.getLicense,
                credentialStore,
                administrationData.message.data.license_id,
            );

            yield put({ type: 'DATA_LOADING', resource: 'license' });

            if (licenseData.error) {
                yield put({ type: 'ERROR', errors: licenseData, dataType: 'alerts' });
            } else {
                yield put({ type: 'SUCCESS', license: licenseData, dataType: 'license' });
                yield put({ type: 'DATA_LOADED', resource: 'license' });
            }
        }

        // Breadcrumbs are out of sync after changing related company or license:
        // Just always reset after loading all details.
        yield put({ type: 'RESET_BREADCRUMBS', data: {} });
    }
}

function* postAdministrationsFlow(credentialStore) {
    while (true) {
        const { data } = yield take('POST_ADMINISTRATION');

        yield put({ type: 'SET_DATA', resource: 'administrationNew' });

        const administrationNewData = yield callHUB(HUB.postAdministration, credentialStore, data);

        if (administrationNewData.error) {
            yield put({ type: 'ERROR', errors: administrationNewData, dataType: 'alerts' });
            yield put({ type: 'SET_DATA_ERROR', resource: 'administrationNew' });
        } else {
            createAlert('success', i18n.translate('administration.alert.success.save'), administrationNewData);
            yield put({ type: 'SUCCESS', success: administrationNewData, dataType: 'alerts' });

            yield put({ type: 'SET_DATA_SUCCESS', resource: 'administrationNew' });
            yield put({ type: 'RESET', dataType: 'administrationNew' });

            yield put(
                RouterPush(
                    '/companies/' +
                        administrationNewData.message.data.company_id +
                        '/licenses/' +
                        administrationNewData.message.data.license_id +
                        '/administrations/' +
                        administrationNewData.message.data.id,
                ),
            );
            yield put({ type: 'LOAD_ADMINISTRATIONS' });
        }
    }
}

function* putAdministrationsFlow(credentialStore) {
    while (true) {
        const { data } = yield take('PUT_ADMINISTRATION');

        yield put({ type: 'SET_DATA', resource: 'administrationEdit' });

        const administrationEditData = yield callHUB(HUB.putAdministration, credentialStore, data);

        if (administrationEditData.error) {
            yield put({ type: 'ERROR', errors: administrationEditData, dataType: 'alerts' });
            yield put({ type: 'SET_DATA_ERROR', resource: 'administrationEdit' });
        } else {
            createAlert('success', i18n.translate('general.alert.success.editData'), administrationEditData);
            yield put({ type: 'SUCCESS', success: administrationEditData, dataType: 'alerts' });

            yield put({ type: 'SET_DATA_SUCCESS', resource: 'administrationEdit' });
            yield put({ type: 'RESET', dataType: 'administrationEdit' });

            yield put({ type: 'RESET_ADMINISTRATION_DATA', data: {} });
            yield put({ type: 'RESET_BREADCRUMBS', data: {} });

            yield put(
                RouterPush(
                    '/companies/' +
                        administrationEditData.message.data.company_id +
                        '/licenses/' +
                        administrationEditData.message.data.license_id +
                        '/administrations/' +
                        administrationEditData.message.data.id,
                ),
            );
        }
    }
}

function* licensesFlow(credentialStore) {
    while (true) {
        const { data } = yield take('LOAD_LICENSES');

        yield put({ type: 'DATA_LOADING', resource: 'licenses' });

        const licensesData = yield callHUB(HUB.getLicenses, credentialStore);

        if (licensesData.error) {
            yield put({ type: 'ERROR', errors: companyData, dataType: 'alerts' });
        } else {
            yield put({ type: 'SUCCESS', licenses: licensesData, dataType: 'licenses' });
            yield put({ type: 'DATA_LOADED', resource: 'licenses' });
        }
    }
}

function* licenseFlow(credentialStore) {
    while (true) {
        const { data } = yield take('LOAD_LICENSE');

        yield put({ type: 'DATA_LOADING', resource: 'license' });

        const licenseData = yield callHUB(HUB.getLicense, credentialStore, data.licenseId);
        if (licenseData.error) {
            yield put({ type: 'ERROR', errors: licenseData, dataType: 'alerts' });
        } else {
            yield put({ type: 'SUCCESS', license: licenseData, dataType: 'license' });
            yield put({ type: 'DATA_LOADED', resource: 'license' });
        }
    }
}

function* licenseWithAllModulesFlow(credentialStore) {
    while (true) {
        const { data } = yield take('LOAD_LICENSE_WITH_ALL_MODULES');

        yield put({ type: 'DATA_LOADING', resource: 'licenseWithAllModules' });

        const [licenseData, moduleData] = yield all([
            call(HUB.getLicense, credentialStore, data.licenseId),
            call(HUB.getModules, credentialStore),
        ]);
        if (licenseData.error) {
            yield put({ type: 'ERROR', errors: licenseData, dataType: 'errors' });
            continue;
        }

        if (moduleData.error) {
            yield put({ type: 'ERROR', errors: moduleData, dataType: 'errors' });
            continue;
        }

        yield put({ type: 'SUCCESS', modules: moduleData, dataType: 'modules' });
        yield put({ type: 'SUCCESS', license: licenseData, dataType: 'licenseWithAllModules' });
        yield put({ type: 'DATA_LOADED', resource: 'licenseWithAllModules' });
    }
}

function* postLicenseFlow(credentialStore) {
    while (true) {
        const { data } = yield take('POST_LICENSE');

        yield put({ type: 'SET_DATA', resource: 'licenseNewEdit' });

        const licenseNewData = yield callHUB(HUB.postLicense, credentialStore, data);

        if (licenseNewData.error) {
            yield put({ type: 'ERROR', errors: licenseNewData, dataType: 'alerts' });
            yield put({ type: 'SET_DATA_ERROR', resource: 'licenseNewEdit' });
        } else {
            createAlert('success', i18n.translate('license.alert.success.save'), licenseNewData);
            yield put({ type: 'SUCCESS', success: licenseNewData, dataType: 'alerts' });

            yield put({ type: 'SET_DATA_SUCCESS', resource: 'licenseNewEdit' });
            yield put({ type: 'RESET', dataType: 'licenseNewEdit' });

            yield put(
                RouterPush(
                    '/companies/' +
                        licenseNewData.message.data.company_id +
                        '/licenses/' +
                        licenseNewData.message.data.id,
                ),
            );
        }
    }
}

function* putLicenseFlow(credentialStore) {
    while (true) {
        const { data } = yield take('PUT_LICENSE');

        yield put({ type: 'SET_DATA', resource: 'licenseNewEdit' });

        const licenseEditData = yield callHUB(HUB.putLicense, credentialStore, data);

        if (licenseEditData.error) {
            yield put({ type: 'ERROR', errors: licenseEditData, dataType: 'alerts' });
            yield put({ type: 'SET_DATA_ERROR', resource: 'licenseNewEdit' });
        } else {
            createAlert('success', i18n.translate('general.alert.success.editData'), licenseEditData);
            yield put({ type: 'SUCCESS', success: licenseEditData, dataType: 'alerts' });

            yield put({ type: 'SET_DATA_SUCCESS', resource: 'licenseNewEdit' });
            yield put({ type: 'RESET', dataType: 'licenseNewEdit' });

            yield put({ type: 'RESET_LICENSE_DATA', data: {} });
            yield put({ type: 'RESET_BREADCRUMBS', data: {} });
            yield put(RouterPush('/companies/' + data.companyId + '/licenses/' + data.licenseId));
        }
    }
}

function* deleteLicenseFlow(credentialStore) {
    while (true) {
        const { data } = yield take('DELETE_LICENSE');

        yield put({ type: 'DATA_DELETE', resource: 'license' });

        const licenseData = yield callHUB(HUB.deleteLicense, credentialStore, data);

        if (licenseData.error) {
            yield put({ type: 'ERROR', errors: licenseData, dataType: 'alerts' });
        } else {
            createAlert('success', i18n.translate('license.alert.success.delete'), licenseData);
            yield put({ type: 'SUCCESS', success: licenseData, dataType: 'alerts' });

            yield put({ type: 'DELETE_DATA_SUCCESS', resource: 'license' });
            yield put(RouterPush('/companies/' + data.companyId));
        }
    }
}

function* postScheduledLicenseFlow(credentialStore) {
    while (true) {
        const { data } = yield take('POST_SCHEDULED_CHANGE_OF_LICENSE');

        yield put({ type: 'SET_DATA', resource: 'licenseNewEdit' });

        const licenseNewScheduledChangeData = yield callHUB(HUB.postScheduledChangeOfLicense, credentialStore, data);

        if (licenseNewScheduledChangeData.error) {
            yield put({ type: 'ERROR', errors: licenseNewScheduledChangeData, dataType: 'alerts' });
            yield put({ type: 'SET_DATA_ERROR', resource: 'licenseNewEdit' });
        } else {
            createAlert(
                'success',
                'De geplande wijziging van deze licentie is toegevoegd',
                licenseNewScheduledChangeData,
            );
            yield put({ type: 'SUCCESS', success: licenseNewScheduledChangeData, dataType: 'alerts' });

            yield put({ type: 'SET_DATA_SUCCESS', resource: 'licenseNewEdit' });
            yield put({ type: 'RESET', dataType: 'licenseNewEdit' });

            yield put(RouterPush('/companies/' + data.companyId + '/licenses/' + data.licenseId));
        }
    }
}

function* modulesFlow(credentialStore) {
    while (true) {
        const { data } = yield take('LOAD_MODULES');

        yield put({ type: 'DATA_LOADING', resource: 'modules' });

        const modulesData = yield callHUB(HUB.getModules, credentialStore, data);

        if (modulesData.error) {
            yield put({ type: 'ERROR', errors: modulesData, dataType: 'alerts' });
        } else {
            yield put({ type: 'SUCCESS', modules: modulesData, dataType: 'modules' });
            yield put({ type: 'DATA_LOADED', resource: 'modules' });
        }
    }
}

function* deleteScheduledLicenseChangeFlow(credentialStore) {
    while (true) {
        const { data } = yield take('DELETE_SCHEDULED_LICENSE_CHANGE');

        yield put({ type: 'DATA_DELETE', resource: 'license' });

        const licenseData = yield callHUB(HUB.deleteScheduledLicenseChange, credentialStore, data);

        if (licenseData.error) {
            yield put({ type: 'ERROR', errors: licenseData, dataType: 'alerts' });
        } else {
            createAlert('success', i18n.translate('license.alert.success.deleteScheduledChange'), licenseData);
            yield put({ type: 'SUCCESS', success: licenseData, dataType: 'alerts' });

            yield put({ type: 'DELETE_DATA_SUCCESS', resource: 'license' });
            yield put({
                type: 'LOAD_LICENSE',
                data: {
                    licenseId: data.licenseId,
                },
            });
        }
    }
}

function* applicationsFlow(credentialStore) {
    while (true) {
        const { data } = yield take('LOAD_APPLICATIONS');

        yield put({ type: 'DATA_LOADING', resource: 'applications' });
        yield put({ type: 'LOAD_ALL_TEMPLATES' });

        const applicationsData = yield callHUB(HUB.getAdministrationApplications, credentialStore, data);

        if (applicationsData.error) {
            yield put({ type: 'ERROR', errors: applicationsData, dataType: 'alerts' });
            yield put({ type: 'DATA_LOAD_ERROR', resource: 'applications' });
        } else {
            yield put({ type: 'SUCCESS', applications: applicationsData, dataType: 'applications' });
            yield put({ type: 'DATA_LOADED', resource: 'applications' });
        }
    }
}

function* applicationFlow(credentialStore) {
    while (true) {
        const { data } = yield take('LOAD_APPLICATION');

        yield put({ type: 'DATA_LOADING', resource: 'application' });

        const applicationData = yield callHUB(
            HUB.getAdministrationApplication,
            credentialStore,
            data.administrationId,
            data.applicationId,
        );

        if (applicationData.error) {
            yield put({ type: 'ERROR', errors: applicationData, dataType: 'alerts' });
        } else {
            yield put({ type: 'SUCCESS', application: applicationData, dataType: 'application' });
            yield put({ type: 'DATA_LOADED', resource: 'application' });
        }
    }
}

function* postApplicationFlow(credentialStore) {
    while (true) {
        const { data } = yield take('POST_APPLICATION');

        yield put({ type: 'SET_DATA', resource: 'applicationNewEdit' });

        const applicationNewData = yield callHUB(HUB.postAdministrationApplication, credentialStore, data);

        if (applicationNewData.error) {
            yield put({ type: 'ERROR', errors: applicationNewData, dataType: 'alerts' });
            yield put({ type: 'SET_DATA_ERROR', resource: 'applicationNewEdit' });
        } else {
            createAlert('success', i18n.translate('application.alert.success.save'), applicationNewData);
            yield put({ type: 'SUCCESS', success: applicationNewData, dataType: 'alerts' });

            yield put({ type: 'SET_DATA_SUCCESS', resource: 'applicationNewEdit' });
            yield put({ type: 'RESET', dataType: 'applicationNewEdit' });

            yield put({ type: 'REFRESH_ADMINISTRATIONS', data: {} });
            yield put(
                RouterPush(
                    '/companies/' +
                        data.companyId +
                        '/licenses/' +
                        data.licenseId +
                        '/administrations/' +
                        data.administrationId +
                        '/applications/' +
                        applicationNewData.message.data.id,
                ),
            );
        }
    }
}

function* postApplicationWithApiUser(credentialStore) {
    while (true) {
        const { data } = yield take('POST_APPLICATION_WITH_API_USER');

        yield put({ type: 'SET_DATA', resource: 'applicationNewEdit' });

        const applicationNewData = yield callHUB(HUB.postAdministrationApplication, credentialStore, data.application);

        if (applicationNewData.error) {
            yield put({ type: 'ERROR', errors: applicationNewData, dataType: 'alerts' });
            yield put({ type: 'SET_DATA_ERROR', resource: 'applicationNewEdit' });
        } else {
            yield put({ type: 'SUCCESS', success: applicationNewData, dataType: 'alerts' });
            yield put({ type: 'SET_DATA_SUCCESS', resource: 'applicationNewEdit' });

            // Add a new API-user and connect it to the just added application
            yield put({ type: 'SET_DATA', resource: 'apiUserNewEdit' });

            data.apiUser.applicationId = applicationNewData.message.data.id;
            const apiUserNewData = yield callHUB(
                HUB.postAdministrationsApplicationApiUser,
                credentialStore,
                data.apiUser,
            );

            if (apiUserNewData.error) {
                yield put({ type: 'ERROR', errors: apiUserNewData, dataType: 'alerts' });
                yield put({ type: 'SET_DATA_ERROR', resource: 'apiUserNewEdit' });

                // If adding a new API-user somehow fails, delete the just added application to avoid duplicate data
                yield callHUB(
                    HUB.deleteAdministrationApplication,
                    credentialStore,
                    data.application.administrationId,
                    applicationNewData.message.data.id,
                );
            } else {
                createAlert('success', i18n.translate('application.alert.success.save'), apiUserNewData);

                yield put({ type: 'SET_DATA_SUCCESS', resource: 'apiUserNewEdit' });
                yield put({ type: 'SUCCESS', success: apiUserNewData, dataType: 'alerts' });

                yield put({ type: 'RESET', dataType: 'applicationNewEdit' });
                yield put({ type: 'RESET', dataType: 'apiUserNewEdit' });

                yield put({ type: 'REFRESH_ADMINISTRATIONS', data: {} });
                yield put(
                    RouterPush(
                        '/companies/' +
                            data.application.companyId +
                            '/licenses/' +
                            data.application.licenseId +
                            '/administrations/' +
                            data.application.administrationId +
                            '/applications/' +
                            applicationNewData.message.data.id,
                    ),
                );
            }
        }
    }
}

function* putApplicationFlow(credentialStore) {
    while (true) {
        const { data } = yield take('PUT_APPLICATION');

        yield put({ type: 'SET_DATA', resource: 'applicationNewEdit' });

        const applicationEditData = yield callHUB(HUB.putAdministrationApplication, credentialStore, data.application);

        if (applicationEditData.error) {
            yield put({ type: 'ERROR', errors: applicationEditData, dataType: 'alerts' });
            yield put({ type: 'SET_DATA_ERROR', resource: 'applicationNewEdit' });
        } else {
            createAlert('success', i18n.translate('general.alert.success.editData'), applicationEditData);
            yield put({ type: 'SUCCESS', success: applicationEditData, dataType: 'alerts' });

            yield put({ type: 'SET_DATA_SUCCESS', resource: 'applicationNewEdit' });
            yield put({ type: 'RESET', dataType: 'applicationNewEdit' });

            yield put({ type: 'RESET_APPLICATION_DATA', data: {} });
            yield put({ type: 'RESET_BREADCRUMBS', data: {} });

            yield put(
                RouterPush(
                    '/companies/' +
                        data.application.companyId +
                        '/licenses/' +
                        data.application.licenseId +
                        '/administrations/' +
                        data.application.administrationId +
                        '/applications/' +
                        applicationEditData.message.data.id,
                ),
            );
        }
    }
}

function* deleteApplicationFlow(credentialStore) {
    while (true) {
        const { data } = yield take('DELETE_APPLICATION');

        yield put({ type: 'DATA_DELETE', resource: 'application' });

        const applicationData = yield callHUB(
            HUB.deleteAdministrationApplication,
            credentialStore,
            data.administrationId,
            data.applicationId,
        );

        if (applicationData.error) {
            yield put({ type: 'ERROR', errors: applicationData, dataType: 'alerts' });
        } else {
            createAlert('success', i18n.translate('application.alert.success.delete'), applicationData);
            yield put({ type: 'SUCCESS', success: applicationData, dataType: 'alerts' });

            yield put({ type: 'DELETE_DATA_SUCCESS', resource: 'application' });
            yield put(
                RouterPush(
                    '/companies/' +
                        data.companyId +
                        '/licenses/' +
                        data.licenseId +
                        '/administrations/' +
                        data.administrationId,
                ),
            );
        }
    }
}

function* websitesFlow(credentialStore) {
    while (true) {
        const { data } = yield take('LOAD_WEBSITES');

        yield put({ type: 'DATA_LOADING', resource: 'websites' });

        // Don't transform the metadata if there isn't any
        const websitesData = yield callHUB(HUB.getAdministrationWebsites, credentialStore, data);

        if (websitesData.error) {
            yield put({ type: 'ERROR', errors: websitesData, dataType: 'alerts' });
            yield put({ type: 'DATA_LOAD_ERROR', resource: 'websites' });
        } else {
            yield put({ type: 'SUCCESS', websites: websitesData, dataType: 'websites' });
            yield put({ type: 'DATA_LOADED', resource: 'websites' });
        }
    }
}

function* apiUsersFlow(credentialStore) {
    while (true) {
        const { data } = yield take('LOAD_API_USERS');

        yield put({ type: 'DATA_LOADING', resource: 'apiUsers' });

        const apiUsersData = yield callHUB(HUB.getAdministrationsApiUsers, credentialStore, data);

        if (apiUsersData.error) {
            yield put({ type: 'ERROR', errors: apiUsersData, dataType: 'alerts' });
            yield put({ type: 'DATA_LOAD_ERROR', resource: 'apiUsers' });
        } else {
            yield put({ type: 'SUCCESS', apiUsers: apiUsersData, dataType: 'apiUsers' });
            yield put({ type: 'DATA_LOADED', resource: 'apiUsers' });
        }
    }
}

function* apiUserFlow(credentialStore) {
    while (true) {
        const { data } = yield take('LOAD_API_USER');

        yield put({ type: 'DATA_LOADING', resource: 'apiUser' });

        const apiUserData = yield callHUB(
            HUB.getAdministrationsApplicationApiUser,
            credentialStore,
            data.administrationId,
            data.apiUserId,
        );

        if (apiUserData.error) {
            yield put({ type: 'ERROR', errors: apiUserData, dataType: 'alerts' });
        } else {
            yield put({ type: 'SUCCESS', apiUser: apiUserData, dataType: 'apiUser' });
            yield put({ type: 'DATA_LOADED', resource: 'apiUser' });
        }
    }
}

function* apiUserWithAllRoutingSlugsFlow(credentialStore) {
    while (true) {
        const { data } = yield take('LOAD_API_USER_WITH_ALL_ROUTING_SLUGS');

        yield put({ type: 'DATA_LOADING', resource: 'apiUserWithAllRoutingSlugs' });

        const slugs = yield routingSlugsFromVersion(HUB.getRoutingSlugs, credentialStore);
        const apiUserData = yield callHUB(
            HUB.getAdministrationsApplicationApiUser,
            credentialStore,
            data.administrationId,
            data.apiUserId,
        );

        yield put({ type: 'SUCCESS', apiUser: apiUserData, slugs: slugs, dataType: 'apiUserWithAllRoutingSlugs' });
        yield put({ type: 'DATA_LOADED', resource: 'apiUserWithAllRoutingSlugs' });
    }
}

function* postApiUserFlow(credentialStore) {
    while (true) {
        const { data } = yield take('POST_API_USER');

        yield put({ type: 'SET_DATA', resource: 'apiUserNewEdit' });

        const apiUserNewData = yield callHUB(HUB.postAdministrationsApplicationApiUser, credentialStore, data);

        if (apiUserNewData.error) {
            yield put({ type: 'ERROR', errors: apiUserNewData, dataType: 'alerts' });
            yield put({ type: 'SET_DATA_ERROR', resource: 'apiUserNewEdit' });
        } else {
            createAlert('success', i18n.translate('apiUser.alert.success.save'), apiUserNewData);
            yield put({ type: 'SUCCESS', success: apiUserNewData, dataType: 'alerts' });

            yield put({ type: 'SET_DATA_SUCCESS', resource: 'apiUserNewEdit' });
            yield put({ type: 'RESET', dataType: 'apiUserNewEdit' });

            yield put(
                RouterPush(
                    '/companies/' +
                        data.companyId +
                        '/licenses/' +
                        data.licenseId +
                        '/administrations/' +
                        data.administrationId +
                        '/api_users/' +
                        apiUserNewData.message.data.id,
                ),
            );
        }
    }
}

function* putApiUserFlow(credentialStore) {
    while (true) {
        const { data } = yield take('PUT_API_USER');

        yield put({ type: 'SET_DATA', resource: 'apiUserNewEdit' });

        const apiUserEditData = yield callHUB(HUB.putAdministrationsApplicationApiUser, credentialStore, data);

        if (apiUserEditData.error) {
            yield put({ type: 'ERROR', errors: apiUserEditData, dataType: 'alerts' });
            yield put({ type: 'SET_DATA_ERROR', resource: 'apiUserNewEdit' });
        } else {
            createAlert('success', i18n.translate('general.alert.success.editData'), apiUserEditData);
            yield put({ type: 'SUCCESS', success: apiUserEditData, dataType: 'alerts' });

            yield put({ type: 'SET_DATA_SUCCESS', resource: 'apiUserNewEdit' });
            yield put({ type: 'RESET', dataType: 'apiUserNewEdit' });

            yield put({ type: 'RESET_API_USER_DATA', data: {} });
            yield put({ type: 'RESET_BREADCRUMBS', data: {} });

            yield put(
                RouterPush(
                    '/companies/' +
                        data.companyId +
                        '/licenses/' +
                        data.licenseId +
                        '/administrations/' +
                        data.administrationId +
                        '/api_users/' +
                        apiUserEditData.message.data.id,
                ),
            );
        }
    }
}

function* deleteApiUserFlow(credentialStore) {
    while (true) {
        const { data } = yield take('DELETE_API_USER');

        yield put({ type: 'DATA_DELETE', resource: 'apiUser' });

        const apiUserData = yield callHUB(
            HUB.deleteAdministrationsApplicationApiUser,
            credentialStore,
            data.administrationId,
            data.applicationId,
            data.apiUserId,
        );

        if (apiUserData.error) {
            yield put({ type: 'ERROR', errors: apiUserData, dataType: 'alerts' });
        } else {
            createAlert('success', i18n.translate('apiUser.alert.success.delete'), apiUserData);
            yield put({ type: 'SUCCESS', success: apiUserData, dataType: 'alerts' });

            yield put({ type: 'DELETE_DATA_SUCCESS', resource: 'apiUser' });

            const path = data.applicationId ? `/applications/${data.applicationId}` : '';
            yield put(
                RouterPush(
                    '/companies/' +
                        data.companyId +
                        '/licenses/' +
                        data.licenseId +
                        '/administrations/' +
                        data.administrationId +
                        path,
                ),
            );
        }
    }
}

function* routingSlugsFlow(credentialStore) {
    while (true) {
        const { data } = yield take('LOAD_ROUTING_SLUGS');

        yield put({ type: 'DATA_LOADING', resource: 'routingSlugs' });

        const slugs = yield routingSlugsFromVersion(HUB.getRoutingSlugs, credentialStore);

        yield put({ type: 'SUCCESS', slugs: slugs, dataType: 'routingSlugs' });
        yield put({ type: 'DATA_LOADED', resource: 'routingSlugs' });
    }
}

function* routingSlugsFromVersion(hubAction, credentialStore) {
    const slugData = yield call(hubAction, credentialStore);
    if (slugData.error) {
        yield put({ type: 'ERROR', errors: slugData, dataType: 'alerts' });
    } else {
        // Fetch all other routes if there are more than the hard-coded limit of 100
        const metadata = slugData.message.meta;
        const metadataLimit = parseInt(metadata.limit, 10);
        const metadataOffset = parseInt(metadata.offset, 10);
        let amountOfResults = metadataLimit + metadataOffset;

        while (amountOfResults < metadata.total) {
            // Calculate the new offset
            const data = {};
            data.offset = amountOfResults;

            const extraRoutingSlugsData = yield callHUB(hubAction, credentialStore, data);

            // Fuse the newly fetched data to the existing routing slugs array
            slugData.message.data.push(...extraRoutingSlugsData.message.data);

            // Update the amount of results
            const newOffset = parseInt(extraRoutingSlugsData.message.meta.offset, 10);
            amountOfResults = metadataLimit + newOffset;
        }
        return slugData;
    }
}

function* updateBreadcrumbsFlow(credentialStore) {
    while (true) {
        const { data } = yield take('UPDATE_BREADCRUMBS');

        const state = yield select(getState);

        // Set-up the data object
        const breadcrumbsData = {};
        breadcrumbsData.company = {};
        breadcrumbsData.license = {};
        breadcrumbsData.administration = {};
        breadcrumbsData.apiUser = {};

        // Fetch the company data if it has changed
        if (data.company.id && data.company.id != 'null') {
            if (!state.company || data.company.id != state.company.id) {
                const companyData = yield callHUB(HUB.getCompany, credentialStore, data.company.id);
                breadcrumbsData.company.id = companyData.message.data.id;
                breadcrumbsData.company.name = companyData.message.data.name;
                breadcrumbsData.company.debtorNumber = companyData.message.data.debtor_number;
            } else if (state.company.id) {
                breadcrumbsData.company.id = state.company.id;
                breadcrumbsData.company.name = state.company.name;
                breadcrumbsData.company.debtorNumber = state.company.debtorNumber;
            }
        } else {
            breadcrumbsData.company.id = null;
            breadcrumbsData.company.name = 'Geen bedrijf gekoppeld';
        }

        // Fetch the license data if it has changed
        if (data.license.id && data.license.id != 'null') {
            if (!state.license || data.license.id != state.license.id) {
                const licenseData = yield callHUB(HUB.getLicense, credentialStore, data.license.id);

                breadcrumbsData.license.id = licenseData.message.data.id;
                breadcrumbsData.license.description = licenseData.message.data.description;
            } else if (state.license.id) {
                breadcrumbsData.license.id = state.license.id;
                breadcrumbsData.license.description = state.license.description;
            }
        } else {
            breadcrumbsData.license.id = null;
            breadcrumbsData.license.description = 'Geen licentie gekoppeld';
        }

        // Fetch the administration data if it has changed
        if (data.administration.id && data.administration.id != 'null') {
            if (!state.administration || data.administration.id != state.administration.id) {
                const administrationData = yield callHUB(
                    HUB.getAdministration,
                    credentialStore,
                    data.administration.id,
                );

                breadcrumbsData.administration.id = administrationData.message.data.id;
                breadcrumbsData.administration.name = administrationData.message.data.name;
            } else if (state.administration.id) {
                breadcrumbsData.administration.id = state.administration.id;
                breadcrumbsData.administration.name = state.administration.name;
            }
        } else {
            breadcrumbsData.administration.id = null;
            breadcrumbsData.administration.name = 'Geen administratie gekoppeld';
        }

        // Fetch the API-user data if it has changed
        if (data.administration.id && data.apiUser.id && data.apiUser.id != 'null') {
            if (!state.apiUser || data.apiUser.id != state.apiUser.id) {
                const apiUserData = yield callHUB(
                    HUB.getAdministrationsApplicationApiUser,
                    credentialStore,
                    data.administration.id,
                    data.apiUser.id,
                );

                breadcrumbsData.apiUser.id = apiUserData.message.data.id;
                breadcrumbsData.apiUser.name = apiUserData.message.data.name;
            } else if (state.apiUser.id) {
                breadcrumbsData.apiUser.id = state.apiUser.id;
                breadcrumbsData.apiUser.name = state.apiUser.name;
            }
        } else {
            breadcrumbsData.apiUser.id = null;
            breadcrumbsData.apiUser.name = 'Geen API-user gekoppeld';
        }
        yield put({ type: 'SUCCESS', breadcrumbs: breadcrumbsData, dataType: 'breadcrumbs' });
    }
}

function* searchDataFlow(credentialStore) {
    while (true) {
        const { data } = yield take('LOAD_SEARCH_RESULTS');

        yield put({ type: 'DATA_LOADING', resource: 'searchResults' });

        const [companiesData, administrationsData] = yield all([
            call(HUB.getCompanies, credentialStore, data),
            call(HUB.getAdministrations, credentialStore, data),
        ]);
        if (companiesData.error) {
            yield put({ type: 'ERROR', errors: companiesData, dataType: 'errors' });
            continue;
        }

        if (administrationsData.error) {
            yield put({ type: 'ERROR', errors: administrationsData, dataType: 'errors' });
            continue;
        }

        yield put({
            type: 'SUCCESS',
            companies: companiesData,
            administrations: administrationsData,
            dataType: 'searchResults',
        });
        yield put({ type: 'DATA_LOADED', resource: 'searchResults' });
    }
}

function* templatesFlow(credentialStore) {
    while (true) {
        const { data } = yield take('LOAD_TEMPLATES');

        yield put({ type: 'DATA_LOADING', resource: 'templates' });

        const templatesData = yield callHUB(HUB.getTemplates, credentialStore, data);

        if (templatesData.error) {
            yield put({ type: 'ERROR', errors: templatesData, dataType: 'alerts' });
            yield put({ type: 'DATA_LOAD_ERROR', resource: 'templates' });
        } else {
            yield put({ type: 'SUCCESS', templates: templatesData, dataType: 'templates' });
            yield put({ type: 'DATA_LOADED', resource: 'templates' });
        }
    }
}

function* allTemplatesFlow(credentialStore) {
    while (true) {
        const { data } = yield take('LOAD_ALL_TEMPLATES');

        yield put({ type: 'DATA_LOADING', resource: 'templates' });

        let page = 1;
        const resultsPerPage = 25;
        const metadata = {
            meta: {
                resultsPerPage: resultsPerPage,
                page: page,
            },
        };

        const templatesData = yield callHUB(HUB.getTemplates, credentialStore, metadata);

        if (templatesData.error) {
            yield put({ type: 'ERROR', errors: templatesData, dataType: 'alerts' });
            yield put({ type: 'DATA_LOAD_ERROR', resource: 'templates' });
        } else {
            // Fetch all other templates if there are more than the hard-coded limit of 100
            const metadata = templatesData.message.meta;
            const metadataLimit = parseInt(metadata.limit, 10);
            const metadataOffset = parseInt(metadata.offset, 10);
            let amountOfResults = metadataLimit + metadataOffset;

            while (amountOfResults < metadata.total) {
                // Get the next page of data
                page++;
                const newMetadata = {
                    meta: {
                        resultsPerPage: resultsPerPage,
                        page: page,
                    },
                };

                const extraTemplatesData = yield callHUB(HUB.getTemplates, credentialStore, newMetadata);

                // Fuse the newly fetched data to the existing routing slugs array
                templatesData.message.data.push(...extraTemplatesData.message.data);

                // Update the amount of results
                const newOffset = parseInt(extraTemplatesData.message.meta.offset, 10);
                amountOfResults = metadataLimit + newOffset;
            }

            yield put({ type: 'SUCCESS', templates: templatesData, dataType: 'templates' });
            yield put({ type: 'DATA_LOADED', resource: 'templates' });
        }
    }
}

function* templateFlow(credentialStore) {
    while (true) {
        const { data } = yield take('LOAD_TEMPLATE');

        yield put({ type: 'DATA_LOADING', resource: 'template' });

        const templateData = yield callHUB(HUB.getTemplate, credentialStore, data.templateId);

        if (templateData.error) {
            yield put({ type: 'ERROR', errors: templateData, dataType: 'alerts' });
            yield put({ type: 'DATA_LOAD_ERROR', resource: 'template' });
        } else {
            yield put({ type: 'SUCCESS', template: templateData, dataType: 'template' });
            yield put({ type: 'DATA_LOADED', resource: 'template' });
        }
    }
}

function* templateWithAllRoutingSlugsFlow(credentialStore) {
    while (true) {
        const { data } = yield take('LOAD_TEMPLATE_WITH_ALL_ROUTING_SLUGS');

        yield put({ type: 'DATA_LOADING', resource: 'templateWithAllRoutingSlugs' });

        const templateData = yield callHUB(HUB.getTemplate, credentialStore, data.templateId);
        const slugs = yield routingSlugsFromVersion(HUB.getRoutingSlugs, credentialStore);

        yield put({ type: 'SUCCESS', template: templateData, slugs: slugs, dataType: 'templateWithAllRoutingSlugs' });
        yield put({ type: 'DATA_LOADED', resource: 'templateWithAllRoutingSlugs' });
    }
}

function* postTemplateFlow(credentialStore) {
    while (true) {
        const { data } = yield take('POST_TEMPLATE');

        yield put({ type: 'SET_DATA', resource: 'templateNewEdit' });
        const templateNewData = yield callHUB(HUB.postTemplate, credentialStore, data);

        if (templateNewData.error) {
            yield put({ type: 'ERROR', errors: templateNewData, dataType: 'alerts' });
            yield put({ type: 'SET_DATA_ERROR', resource: 'templateNewEdit' });
        } else {
            createAlert('success', i18n.translate('template.alert.success.save'), templateNewData);
            yield put({ type: 'SUCCESS', success: templateNewData, dataType: 'alerts' });

            yield put({ type: 'SET_DATA_SUCCESS', resource: 'templateNewEdit' });
            yield put({ type: 'RESET', dataType: 'templateNewEdit' });

            yield put(RouterPush('/templates/' + templateNewData.message.data.id));
        }
    }
}

function* putTemplateFlow(credentialStore) {
    while (true) {
        const { data } = yield take('PUT_TEMPLATE');

        yield put({ type: 'SET_DATA', resource: 'templateNewEdit' });

        const templateEditData = yield callHUB(HUB.putTemplate, credentialStore, data);

        if (templateEditData.error) {
            yield put({ type: 'ERROR', errors: templateEditData, dataType: 'alerts' });
            yield put({ type: 'SET_DATA_ERROR', resource: 'templateNewEdit' });
        } else {
            createAlert('success', i18n.translate('general.alert.success.editData'), templateEditData);
            yield put({ type: 'SUCCESS', success: templateEditData, dataType: 'alerts' });

            yield put({ type: 'SET_DATA_SUCCESS', resource: 'templateNewEdit' });
            yield put({ type: 'RESET', dataType: 'templateNewEdit' });
            yield put({ type: 'RESET', dataType: 'templateWithAllRoutingSlugs' });

            yield put(RouterPush('/templates/' + data.templateId));
        }
    }
}

function* deleteTemplateFlow(credentialStore) {
    while (true) {
        const { data } = yield take('DELETE_TEMPLATE');

        yield put({ type: 'DATA_DELETE', resource: 'template' });

        const templateData = yield callHUB(HUB.deleteTemplate, credentialStore, data.templateId);

        if (templateData.error) {
            yield put({ type: 'ERROR', errors: templateData, dataType: 'alerts' });
        } else {
            createAlert('success', i18n.translate('template.alert.success.delete'), templateData);
            yield put({ type: 'SUCCESS', success: templateData, dataType: 'alerts' });

            yield put({ type: 'RESET', dataType: 'templateNewEdit' });
            yield put({ type: 'RESET', dataType: 'templateWithAllRoutingSlugs' });

            yield put({ type: 'DELETE_DATA_SUCCESS', resource: 'template' });
            yield put(RouterPush('/templates'));
        }
    }
}

function* runningTasksFlow(credentialStore) {
    while (true) {
        const { data } = yield take('LOAD_RUNNING_TASKS');

        yield put({ type: 'DATA_LOADING', resource: 'runningTasks' });

        const result = yield callHUB(HUB.getRunningTasks, credentialStore, data);

        if (result.error) {
            yield put({ type: 'ERROR', errors: result, dataType: 'alerts' });
            yield put({ type: 'DATA_LOAD_ERROR', resource: 'runningTasks' });
        } else {
            yield put({ type: 'SUCCESS', runningTasks: result, dataType: 'runningTasks' });
            yield put({ type: 'DATA_LOADED', resource: 'runningTasks' });
        }
    }
}

function* unlockTaskFlow(credentialStore) {
    while (true) {
        const { data } = yield take('UNLOCK_TASK');

        yield put({ type: 'SET_DATA', resource: 'runningTasks' });

        const result = yield callHUB(HUB.unlockTask, credentialStore, data.taskId);

        if (result.error) {
            yield put({ type: 'ERROR', errors: result, dataType: 'alerts' });
            yield put({ type: 'DATA_LOAD_ERROR', resource: 'runningTasks' });
        } else {
            const loadResult = yield callHUB(HUB.getRunningTasks, credentialStore, data);
            if (loadResult.error) {
                yield put({ type: 'ERROR', errors: loadResult, dataType: 'alerts' });
                yield put({ type: 'DATA_LOAD_ERROR', resource: 'runningTasks' });
            } else {
                yield put({ type: 'SUCCESS', runningTasks: loadResult, dataType: 'runningTasks' });
                yield put({ type: 'DATA_LOADED', resource: 'runningTasks' });
            }
        }
    }
}

function* jobsOverviewFlow(credentialStore) {
    while (true) {
        const { data } = yield take('LOAD_JOBS_OVERVIEW');

        yield put({ type: 'DATA_LOADING', resource: 'jobsOverview' });

        const jobsOverviewData = yield callHUB(HUB.getJobsOverview, credentialStore, data);

        if (jobsOverviewData.error) {
            yield put({ type: 'ERROR', errors: jobsOverviewData, dataType: 'alerts' });
            yield put({ type: 'DATA_LOAD_ERROR', resource: 'jobsOverview' });
        } else {
            yield put({ type: 'SUCCESS', jobsOverview: jobsOverviewData, dataType: 'jobsOverview' });
            yield put({ type: 'DATA_LOADED', resource: 'jobsOverview' });
        }
    }
}

function* failedJobsFlow(credentialStore) {
    while (true) {
        const { data } = yield take('LOAD_FAILED_JOBS');

        yield put({ type: 'DATA_LOADING', resource: 'failedJobs' });

        const failedJobsData = yield callHUB(HUB.getFailedJobs, credentialStore, data);

        if (failedJobsData.error) {
            yield put({ type: 'ERROR', errors: failedJobsData, dataType: 'alerts' });
            yield put({ type: 'DATA_LOAD_ERROR', resource: 'failedJobs' });
        } else {
            yield put({ type: 'SUCCESS', failedJobs: failedJobsData, dataType: 'failedJobs' });
            yield put({ type: 'DATA_LOADED', resource: 'failedJobs' });
        }
    }
}

function* pacoDashboardFlow(credentialStore) {
    while (true) {
        const { payload } = yield take('LOAD_SESSIONS_SOURCE_OVERVIEW');

        yield put({ type: 'DATA_LOADING', resource: 'sessionsSourceOverview' });

        const requestResult = yield callHUB(HUB.getSessionsSourceOverview, credentialStore, payload?.meta?.search);

        if (requestResult.error) {
            yield put({ type: 'ERROR', errors: requestResult, dataType: 'alerts' });
            yield put({ type: 'DATA_LOAD_ERROR', resource: 'sessionsSourceOverview' });
        } else {
            yield put({ type: 'SUCCESS', sessionsSourceOverview: requestResult, dataType: 'sessionsSourceOverview' });
            yield put({ type: 'DATA_LOADED', resource: 'sessionsSourceOverview' });
        }
    }
}

function* provisionSSOFlow(credentialStore) {
    while (true) {
        const { data } = yield take('PROVISION_SSO');

        const result = yield callHUB(HUB.provisionSSO, credentialStore, data.administrationId);

        if (result.error) {
            yield put({ type: 'ERROR', errors: result, dataType: 'alerts' });
        } else {
            yield put({ type: 'SUCCESS', success: result, dataType: 'alerts' });
            yield delay(1500)
            yield put({ type: 'LOAD_ADMINISTRATION_DETAIL',  data: { administrationId:data.administrationId }});
        }
    }
}

function* HUBAdmin(credentialStore) {
    yield fork(changeEnvironmentFlow, credentialStore);
    yield fork(setTokensFlow, credentialStore);
    yield fork(loginFlow, credentialStore);
    yield fork(refreshAuthenticationFlow, credentialStore);
    yield fork(logoutFlow, credentialStore);
    yield fork(companiesFlow, credentialStore);
    yield fork(allCompaniesFlow, credentialStore);
    yield fork(companyFlow, credentialStore);
    yield fork(postCompanyFlow, credentialStore);
    yield fork(putCompanyFlow, credentialStore);
    yield fork(putLicensePackageLinkFlow, credentialStore);
    yield fork(deleteLicensePackageLinkFlow, credentialStore);
    yield fork(putAdministrationPackageLinkFlow, credentialStore);
    yield fork(deleteAdministrationPackageLinkFlow, credentialStore);
    yield fork(administrationsFlow, credentialStore);
    yield fork(allAdministrationsFlow, credentialStore);
    yield fork(administrationFlow, credentialStore);
    yield fork(administrationDetailFlow, credentialStore);
    yield fork(postAdministrationsFlow, credentialStore);
    yield fork(putAdministrationsFlow, credentialStore);
    yield fork(licensesFlow, credentialStore);
    yield fork(licenseFlow, credentialStore);
    yield fork(licenseWithAllModulesFlow, credentialStore);
    yield fork(postLicenseFlow, credentialStore);
    yield fork(putLicenseFlow, credentialStore);
    yield fork(deleteLicenseFlow, credentialStore);
    yield fork(modulesFlow, credentialStore);
    yield fork(postScheduledLicenseFlow, credentialStore);
    yield fork(deleteScheduledLicenseChangeFlow, credentialStore);
    yield fork(applicationsFlow, credentialStore);
    yield fork(applicationFlow, credentialStore);
    yield fork(postApplicationFlow, credentialStore);
    yield fork(postApplicationWithApiUser, credentialStore);
    yield fork(putApplicationFlow, credentialStore);
    yield fork(deleteApplicationFlow, credentialStore);
    yield fork(websitesFlow, credentialStore);
    yield fork(apiUsersFlow, credentialStore);
    yield fork(apiUserFlow, credentialStore);
    yield fork(apiUserWithAllRoutingSlugsFlow, credentialStore);
    yield fork(postApiUserFlow, credentialStore);
    yield fork(putApiUserFlow, credentialStore);
    yield fork(deleteApiUserFlow, credentialStore);
    yield fork(routingSlugsFlow, credentialStore);
    yield fork(updateBreadcrumbsFlow, credentialStore);
    yield fork(searchDataFlow, credentialStore);
    yield fork(templatesFlow, credentialStore);
    yield fork(allTemplatesFlow, credentialStore);
    yield fork(templateFlow, credentialStore);
    yield fork(templateWithAllRoutingSlugsFlow, credentialStore);
    yield fork(postTemplateFlow, credentialStore);
    yield fork(putTemplateFlow, credentialStore);
    yield fork(deleteTemplateFlow, credentialStore);
    yield fork(resetCompanies, credentialStore);
    yield fork(resetCompany, credentialStore);
    yield fork(resetAdministrations, credentialStore);
    yield fork(resetAdministration, credentialStore);
    yield fork(resetLicense, credentialStore);
    yield fork(resetApplication, credentialStore);
    yield fork(resetApiUser, credentialStore);
    yield fork(resetRoutingSlugs, credentialStore);
    yield fork(resetTemplate, credentialStore);
    yield fork(resetTemplateWithAllRoutes, credentialStore);
    yield fork(resetBreadcrumbs, credentialStore);
    yield fork(resetSearchResults, credentialStore);
    yield fork(jobsOverviewFlow, credentialStore);
    yield fork(failedJobsFlow, credentialStore);
    yield fork(runningTasksFlow, credentialStore);
    yield fork(unlockTaskFlow, credentialStore);
    yield fork(pacoDashboardFlow, credentialStore);
    yield fork(provisionSSOFlow, credentialStore);
}

export { HUBAdmin };
